import { ArrowCircleLeftIcon } from '@common/icons/material/ArrowCircleLeft';
import { ArrowCircleRightIcon } from '@common/icons/material/ArrowCircleRight';
import { SendIcon } from '@common/icons/material/Send';
import React, { useRef, useState, useEffect } from 'react';
import { UwChatDemo } from './chat-uw';
import { SubmitHandler, UseFormReturn, useForm } from 'react-hook-form';
import { useDemoChatUw } from './requests/use-demo-chat-uw';
import { useTrans } from '@common/i18n/use-trans';
import { Form } from '@common/ui/forms/form';
import { FormTextField } from '@common/ui/forms/input-field/text-field/text-field';
import { Button } from '@common/ui/buttons/button';

interface SuggestedPrompt {
  id: string;
  text: string;
}

interface AppraisalReviewCardProps {
  report_id: any;
  sources: any[];
  onSourcesClick?: () => void;
  onPromptChange?: (value: string) => void;
  suggestedPrompts?: any[];
  updateMessage: any;
}

const AppraisalReviewCard: React.FC<AppraisalReviewCardProps> = ({
  report_id,
  sources,
  onSourcesClick,
  onPromptChange,
  suggestedPrompts,
  updateMessage,
}) => {
  

  const form = useForm<UwChatDemo>({
    defaultValues: {
      prompt: '',
      sources: sources,
      report_id: report_id
    },
  });

  const sendChat = useDemoChatUw(form);

  return (
    <ChatPromptForm 
      sources={sources}
      report_id={report_id}
      form={form}
      onSubmit={(values) => {
        sendChat.mutate(values, {
          onSuccess: (response: any) => {
            //updateResult(response);
          },
        });
      }}
      isLoading={sendChat.isPending}
      updateMessage={updateMessage}
      suggestedPrompts={suggestedPrompts}
      onSourcesClick={onSourcesClick}
      onPromptChange={onPromptChange}
    />
  );
};


// Props interface for AddPromptForm, using Prompt directly
interface Props {
  onSubmit: SubmitHandler<UwChatDemo>;
  form: UseFormReturn<UwChatDemo>;
  isLoading?: boolean;
  disableSaveWhenNotDirty?: boolean;
  updateMessage: any;
  // uploadFiles: any;
  // changePrompt: (value: string) => void;
  sources: any[];
  report_id: any;
  suggestedPrompts?: any[];
  onSourcesClick:any;
  onPromptChange: any;
}

// AddPromptForm component
export function ChatPromptForm({
  onSubmit,
  form,
  isLoading = false,
  disableSaveWhenNotDirty = false,
  updateMessage,
  // changePrompt,
  sources,
  report_id,
  suggestedPrompts,
  onSourcesClick,
  onPromptChange
}: Props) {
  const changePromptClick = (text: string) => {
    if (isLoading) return;
  
    form.setValue('prompt', text, { shouldDirty: true });
    form.handleSubmit(onSubmit)();
    updateMessage('Processing...');
  };

  const submitPrompt = () => {
    if (isLoading) return;
  
    form.handleSubmit(onSubmit)();
    updateMessage('Processing...');
  };

  

  const isDirty = !disableSaveWhenNotDirty
    ? true
    : Object.keys(form.formState.dirtyFields).length;

  const { trans } = useTrans();

  const [reportId, setReportId] = useState(report_id ?? '');
  const [status, setStatus] = useState('');
  const [percentage, setPercentage] = useState(0);

  const [sourcesCount, setSourcesCount] = useState(0);
  useEffect(() => {
    setSourcesCount(sources.length);
    form.setValue('sources', sources);
  }, [form, sources]);

  useEffect(() => {
    if (report_id) {
      setReportId(report_id);
      form.setValue('report_id', reportId);
    }
  }, [form, report_id]);

  const handleSend = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1); // -1 for floating point precision
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        ref.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [suggestedPrompts]);

  const scrollLeftHandler = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRightHandler = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const [prompts, setPrompts] = useState(suggestedPrompts?.length ? suggestedPrompts : []);
  useEffect(() => {
    if (suggestedPrompts) {
      setPrompts(suggestedPrompts);
    }
  }, [suggestedPrompts])
  useEffect(() => {
    window.Echo.channel('demo-chat-uw').listen('.update', (e: any) => {
      if (e.data[reportId] != undefined) {
        const item = e.data[reportId];
        // const reportId = item.report_id;
        setReportId(reportId);
        setStatus(item.status);
        setPercentage(item.percentage);
        if (item.message != undefined) {
          updateMessage(item.message);
        }
        if (item.suggested_prompts != undefined) {
          setPrompts(item.suggested_prompts);
        }
      }
    });
  }, [reportId])

  const promptValue = form.watch('prompt');
  


  return (
    <Form
      onSubmit={onSubmit}
      onBeforeSubmit={() => form.clearErrors()}
      form={form}
    >
      <div className={`w-full mx-auto p-6`}>
      {/* Top Input + Sources */}
      <div className="relative mb-6">
        <FormTextField
          name='prompt'
          // value={promptValue}
          // onChange={(e) => onPromptChange?.(e.target.value)}
          // onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), onSend?.())}
          placeholder={'Tell me about...'}
          rows={4}
          inputElementType="textarea"
          // className="px-6 py-5 pr-32 text-lg text-gray-700 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder-gray-400"
        />

        {/* Sources + LARGE Send Button — top-right corner */}
        <div className="absolute right-4 top-4 flex items-center gap-5">
          {/* Sources */}
          <Button
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition"
          >
            <span>{sourcesCount} sources</span>
            {/* <ChevronRightIcon size="md" className="text-gray-600" /> */}
          </Button>

          {/* LARGE & BEAUTIFUL SEND BUTTON */}
          <Button
            onClick={() => submitPrompt()}
            disabled={isLoading || !isDirty ||  sources.length == 0 || !promptValue}
            className={`
              w-14 h-14 rounded-full flex items-center justify-center shadow-lg 
              transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-indigo-300
              ${promptValue.trim() && !isLoading 
                ? 'bg-indigo-600 hover:bg-indigo-700 active:scale-95' 
                : 'bg-gray-300 cursor-not-allowed'
              }
            `}
          >
            {isLoading ? (
              <div className="w-7 h-7 border-3 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <SendIcon 
                size="md" 
                className={promptValue.trim() && sources.length > 0 ? 'text-green' : 'text-gray-500'} 
              />
            )}
          </Button>
        </div>
      </div>

      {/* Horizontal Suggested Prompts with Navigation */}
      <div className="relative">
        {canScrollLeft && (
          <button
            onClick={scrollLeftHandler}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-md flex items-center justify-center border border-green-300 hover:bg-gray-100 transition"
          >
            <ArrowCircleLeftIcon className="text-gray-900" size='lg' />
          </button>
        )}

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth"
        >
          {prompts && (<>
            {prompts.map((prompt, index: number) => (
            <button
              type="button"
              disabled={isLoading || !isDirty ||  sources.length == 0}
              key={`prompt-${index}`}
              onClick={() => changePromptClick(prompt)}
              className="flex-shrink-0 w-[calc(33.33%-1rem)] bg-white rounded-3xl border border-gray-200 p-6 text-left hover:shadow-md transition-shadow relative"
            >
              <p className="text-lg font-semibold text-gray-900 leading-relaxed">
                {prompt}
              </p>
            </button>
          ))}
          </>)}
          
        </div>

        {canScrollRight && (
          <Button
            onClick={scrollRightHandler}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-md flex items-center justify-center border border-green-300 hover:bg-gray-100 transition"
          >
            <ArrowCircleRightIcon className="text-blue-900" size='lg' />
          </Button>
        )}
      </div>
    </div>
    </Form>
  );
};

export default AppraisalReviewCard;